'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Field, fieldClass } from '@/components/platform/agency-fields';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { accountError, ACCOUNT_PASSWORD_MAX, ACCOUNT_PASSWORD_MIN, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parseTenantHealth, type TenantDetail, type TenantHealth } from '@/lib/platform/summary';

export function StaffTab({ slug, detail, onChanged }: { slug: string; detail: TenantDetail | null; onChanged: () => void }) {
  const [resetId, setResetId] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  async function toggle(id: string, active: boolean) {
    setPendingId(id);
    setError(null);
    try {
      await api(`/api/platform/staff/${slug}/${id}`, { method: 'PATCH', body: JSON.stringify({ active }) });
      onChanged();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not update this account.'));
    } finally {
      setPendingId(null);
    }
  }

  async function reset(event: FormEvent, id: string) {
    event.preventDefault();
    if (password.length < ACCOUNT_PASSWORD_MIN || password.length > ACCOUNT_PASSWORD_MAX) {
      setError(`Password must be between ${ACCOUNT_PASSWORD_MIN} and ${ACCOUNT_PASSWORD_MAX} characters.`);
      return;
    }
    setPendingId(id);
    setError(null);
    try {
      await api(`/api/platform/staff/${slug}/${id}`, { method: 'PATCH', body: JSON.stringify({ password }) });
      setPassword('');
      setResetId(null);
      notify('Password updated.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not reset this password.'));
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink">Agency staff</h3>
        {detail?.error === null && !adding && (
          <button type="button" onClick={() => setAdding(true)} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950">
            Invite staff
          </button>
        )}
      </div>
      {detail === null && <p className="text-sm text-muted">Loading staff…</p>}
      {detail?.error && <p className="text-sm text-danger">{detail.error}</p>}
      <ul className="divide-y divide-line">
        {detail?.staff.map((member) => (
          <li key={member.id} className="space-y-2 py-3">
            <div className="flex items-center justify-between gap-3">
              <span>
                <span className="text-sm font-medium text-ink">{member.name}</span>
                <span className="block text-xs text-muted">{member.email} · {member.role === 'super_admin' ? 'Owner' : 'Front desk'}</span>
              </span>
              <span className="flex gap-2">
                <button type="button" disabled={pendingId === member.id} onClick={() => void toggle(member.id, !member.active)} className="rounded-full border border-line px-2 py-1 text-xs text-ink">
                  {member.active ? 'Suspend' : 'Restore'}
                </button>
                <button type="button" onClick={() => { setResetId(member.id); setPassword(''); }} className="rounded-full border border-line px-2 py-1 text-xs text-ink">
                  Reset password
                </button>
              </span>
            </div>
            {resetId === member.id && (
              <form onSubmit={(event) => void reset(event, member.id)} className="flex gap-2">
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" className={fieldClass} placeholder="New password" />
                <button type="submit" disabled={pendingId === member.id} className="rounded-full bg-brand px-3 text-xs font-semibold text-slate-950">Save</button>
              </form>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      {adding && detail?.error === null && (
        <InviteStaff
          slug={slug}
          onCreated={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
}

function InviteStaff({ slug, onCreated, onCancel }: { slug: string; onCreated: () => void; onCancel: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'super_admin' | 'front_desk'>('front_desk');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (firstName.trim() === '' || lastName.trim() === '') {
      setError('Enter a first name and a last name.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/staff/${slug}`, {
        method: 'POST',
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          name: `${firstName.trim()} ${lastName.trim()}`,
          email: email.trim(),
          role,
        }),
      });
      setFirstName('');
      setLastName('');
      setEmail('');
      notify('Invitation sent.');
      onCreated();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not send the invitation.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-3 border-t border-line pt-4">
      <h4 className="text-xs font-semibold text-ink">Invitation</h4>
      <Field label="First name" value={firstName} onChange={setFirstName} />
      <Field label="Last name" value={lastName} onChange={setLastName} />
      <Field label="Email" value={email} onChange={setEmail} type="email" />
      <label className="block text-xs font-medium text-muted">
        Role
        <select value={role} onChange={(event) => setRole(event.target.value === 'super_admin' ? 'super_admin' : 'front_desk')} className={fieldClass}>
          <option value="super_admin">Owner</option>
          <option value="front_desk">Front desk</option>
        </select>
      </label>
      {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={pending} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">
          {pending ? 'Sending…' : 'Send invitation'}
        </button>
        <button type="button" disabled={pending} onClick={onCancel} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
          Cancel
        </button>
      </div>
    </form>
  );
}

export function SiteTab({ slug, closed }: { slug: string; closed: boolean }) {
  if (closed) return <p className="text-sm text-danger">The website stays in the agency database, which could not be opened.</p>;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-ink">Website</h3>
      <p className="text-sm text-muted">
        Build the agency website with the visual site builder. Only platform operators can edit or publish it.
      </p>
      <Link to={`/super-admin/agencies/${slug}/site-builder`} className="inline-flex rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950">
        Open site builder
      </Link>
    </div>
  );
}

export function DatabaseTab({ slug, onRepaired }: { slug: string; onRepaired: () => void }) {
  const [health, setHealth] = useState<TenantHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<unknown>(`/api/platform/tenants/${slug}/health`)
      .then((payload) => {
        if (!cancelled) setHealth(parseTenantHealth(payload));
      })
      .catch((err: unknown) => {
        if (!cancelled && !leaveIfSignedOut(err)) setError(accountError(err, 'Could not check this database.'));
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function repair() {
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/tenants/${slug}/migrate`, { method: 'POST', body: '{}' });
      const next = parseTenantHealth(await api<unknown>(`/api/platform/tenants/${slug}/health`));
      setHealth(next);
      notify('Schema repaired.');
      onRepaired();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not repair this database.'));
    } finally {
      setPending(false);
    }
  }

  async function backup() {
    setPending(true);
    setError(null);
    try {
      await api(`/api/platform/tenants/${slug}/backup`, { method: 'POST', body: '{}' });
      notify('Database archived.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not archive this database.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-ink">Database</h3>
      {health === null && !error && <p className="text-sm text-muted">Checking the connection…</p>}
      {health && (
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted">Status</dt>
            <dd className={`text-sm ${health.status === 'connected' ? 'text-success' : 'text-danger'}`}>{health.status === 'connected' ? 'Connected' : 'Unreachable'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Latency</dt>
            <dd className="text-sm text-ink">{health.latencyMs === null ? '—' : `${health.latencyMs} ms`}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Tables</dt>
            <dd className="text-sm text-ink">{health.tables ?? '—'}</dd>
          </div>
        </dl>
      )}
      {health?.error && <p className="text-sm text-danger">{health.error}</p>}
      {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      <div className="flex gap-2">
        <button type="button" disabled={pending} onClick={() => void repair()} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">
          {pending ? 'Working…' : 'Repair schema'}
        </button>
        <button type="button" disabled={pending} onClick={() => void backup()} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
          Back up this database
        </button>
      </div>
    </div>
  );
}

